import * as React from 'react';
import { connect } from "react-redux";
import { bindActionCreators } from 'redux';
import { RootState } from '../reducers';
import { Popover } from "antd";
import { ReactComponent as Hover } from "../assets/hover.svg"
import { getAllSuspensions, getCustomerSuspensions } from "../actions/orderActions"
import { disableSuspension, getActorRoleString, ENTITY_TYPE, REGION, unwrapSuspensionReason } from './utils';
import { suspensionListInfoType } from '../model/suspensionList';
import { TooltipPlacement } from 'antd/lib/tooltip';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';

interface SuspensionPopoverProps {
    token: string,
    userRole: string,
    suspensionListInfo: suspensionListInfoType
    getAllSuspensions: (domainId: Number | null, accountId: Number | null, authToken: string, suspensionType: string, region: string) => void,
    getCustomerSuspensions: (payload: any, authToken: string) => void,
    shouldShowUnsuspendOption: boolean | null,
    handleUnsuspendClick: () => void | null,
    hoverIconCss: string,
    currentRegion?: string,
    customerIdRegionDetails: any
}

interface ParentProps {
    entityType: ENTITY_TYPE,
    domainId: Number | null,
    accountId: Number | null,
    customerEmail: string | null,
    customerIds: number[] | null
}

interface State {
    visible: boolean
}

type Props = SuspensionPopoverProps & ParentProps & WrappedComponentProps

class SuspensionPopover extends React.Component<Props> {

    state: State = {
        visible: false
    }

    constructor(prop: Props) {
        super(prop)
        this.createContent = this.createContent.bind(this)
        this.fetchSuspensionList = this.fetchSuspensionList.bind(this)
        this.createEmptyContent = this.createEmptyContent.bind(this)
        this.createErrorContent = this.createErrorContent.bind(this)
        this.getSuspensionListObject = this.getSuspensionListObject.bind(this)
        this.unsuspendClick = this.unsuspendClick.bind(this)
    }

    createEmptyContent() {
        return (<div className="suspension-popover-empty-container">
            <div className="loader"></div>
        </div>)
    }

    createErrorContent() {
        return (<div><div className="suspension-popover-error-text">
            <FormattedMessage id="unable-to-fetch-suspensions" />
        </div>
            <button
                className="button button-primary suspension-popover-retry-button"
                onClick={this.fetchSuspensionList}>
                <FormattedMessage id="retry" />
            </button>
        </div>)

    }

    getSuspensionListObject() {
        switch (this.props.entityType) {
            case ENTITY_TYPE.DOMAIN: return this.props.suspensionListInfo.domain;
            case ENTITY_TYPE.ACCOUNT: return this.props.suspensionListInfo.account;
            case ENTITY_TYPE.CUSTOMER: return this.props.suspensionListInfo.customer
        }
    }

    unsuspendClick = () => {
        this.setState({
            visible: false
        })
        this.props.handleUnsuspendClick()
    }

    createContent() {
        const tableHeaders: any = [];
        const suspensionListInfo = this.getSuspensionListObject()
        const fetchSuspensionListInProgress = suspensionListInfo.fetchSuspensionListInProgress
        const fetchSuspensionListError = suspensionListInfo.suspensionListError
        if (fetchSuspensionListInProgress) {
            return this.createEmptyContent()
        }
        else if (fetchSuspensionListError) {
            return this.createErrorContent()
        }
        tableHeaders.push(<th className="suspension-popover-table-header">
            <FormattedMessage id="user" />
        </th>)
        tableHeaders.push(<th className="suspension-popover-table-header">
            <FormattedMessage id="reason" />
        </th>)

        let suspensionsList = suspensionListInfo.suspensionList
        if (this.props.entityType === ENTITY_TYPE.CUSTOMER) {
            let newSuspensionList: any[] = []
            suspensionsList.forEach(s => newSuspensionList.push(...s.details))
            suspensionsList = newSuspensionList
        }
        const allSuspensions = suspensionsList.map((item: any) => {
            const disableRow = disableSuspension(this.props.userRole, item.role)
            const suspensionRoleString = getActorRoleString(item.role, this.props.userRole)
            const suspensionTypeString = this.props.intl.formatMessage({id: unwrapSuspensionReason(item.suspensionType)})
            const disabledNote = this.props.intl.formatMessage({ id: "contact-titan-support-to-unsuspend" })

            const note = item.notes.filter((note: string) => note).join("\n--\n")
            return <tr className="unsuspension-table-data-row-enabled">
                <td>
                    <div className="suspension-popover-table-cell suspension-popover-table-cell-email">
                        <text className="suspension-popover-table-data-heading">
                            {suspensionRoleString}
                        </text>
                        <text className="unsuspension-table-data-subheading">
                            {disableRow ? "" : item.userEmail}
                        </text>
                    </div>
                </td>
                <td>
                    <div className="suspension-popover-table-cell">
                        <text className="suspension-popover-table-data-heading">
                            {suspensionTypeString}
                        </text>
                        <div className="unsuspension-table-data-subheading">
                            {disableRow ? disabledNote : note}
                        </div>
                    </div>
                </td>
            </tr>
        })
        return (
            <div className="suspension-popover-container">
                <div>
                    <div className="suspension-popover-heading">
                        {this.props.entityType === ENTITY_TYPE.CUSTOMER ?
                            <FormattedMessage id="suspension-popover-heading-customer-suspensions" /> :
                            <FormattedMessage id="suspension-popover-heading-suspensions" />}
                        {this.props.shouldShowUnsuspendOption ?
                            <div className="suspension-popover-unsuspend-option" onClick={this.unsuspendClick}>
                                <FormattedMessage id="unsuspend" />
                            </div> : ""}
                    </div>
                    <div className="suspension-popover-table">
                        <table>
                            <tr className="suspension-popover-table-header-row">{tableHeaders}</tr>
                            {allSuspensions}
                        </table>
                    </div>
                </div>
            </div >
        )
    }

    handlePopoverVisibilityChange = () => {
        this.setState((prevState: State) => ({
            visible: !prevState.visible
        }))
        this.fetchSuspensionList()
    }

    fetchSuspensionList() {
        const suspensionListInfo = this.getSuspensionListObject()
        const shouldFetchSuspensionList =
            !suspensionListInfo.fetchSuspensionListInProgress &&
            !suspensionListInfo.fetchSuspensionListSuccess
        if (shouldFetchSuspensionList) {
            if (this.props.entityType === ENTITY_TYPE.CUSTOMER) {
                const customerIds_US = this.props.customerIds ? this.props.customerIds.filter(id => this.props.customerIdRegionDetails[id] === REGION.US) : []
                const customerIds_EU = this.props.customerIds ? this.props.customerIds.filter(id => this.props.customerIdRegionDetails[id] === REGION.EU) : []
                const customerIdRegionPayloadMap: any = {}
                customerIdRegionPayloadMap[REGION.US] = customerIds_US
                customerIdRegionPayloadMap[REGION.EU] = customerIds_EU
                const requestBody = {
                    "customerEmail": this.props.customerEmail,
                    "customerIdsRegionDetails": customerIdRegionPayloadMap
                }
                this.props.getCustomerSuspensions(requestBody, this.props.token)
            }
            else {
                this.props.getAllSuspensions(
                    this.props.domainId, this.props.accountId, this.props.token, this.props.entityType, this.props.currentRegion ? this.props.currentRegion : "")
            }
        }
    }

    render() {
        const popOverContent = this.createContent()
        const tooltipPlacement: TooltipPlacement = this.props.entityType === ENTITY_TYPE.ACCOUNT ? "leftTop" : "rightTop"
        return (
            <div>
                <Popover
                    placement={tooltipPlacement}
                    content={popOverContent}
                    trigger="hover"
                    visible={this.state.visible}
                    onVisibleChange={this.handlePopoverVisibilityChange}>
                    <Hover className={this.props.hoverIconCss} />
                </Popover>
            </div>
        );
    }
}

const mapStateToProps = (state: RootState) => {
    const orders = state.orders as any
    return {
        token: state.auth.token,
        userRole: state.auth.userRole,
        suspensionListInfo: orders.suspensionListInfo,
        customerIdRegionDetails: orders.customerIdRegionDetails
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        ...bindActionCreators({ getAllSuspensions, getCustomerSuspensions }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(SuspensionPopover));